// Server-side token exchange via the Libertify Auth API.
//
// This is the auth path the Libertify web app and our marketing-site
// integration use:
//
//     POST {apiBase}/api/tokens
//     Content-Type: application/x-www-form-urlencoded
//     username={email}&password={password}
//
// The endpoint runs USER_SRP_AUTH server-side, so it works for *every*
// account — including those provisioned via Google / Apple SSO that have a
// password set later. It also bypasses the Pre-Authentication Lambda block
// that rejects browser-side `USER_PASSWORD_AUTH` calls.

import type { Env } from '@/types';
import { getEndpoints } from './endpoints';

export type AuthErrorType =
  | 'InvalidCredentials'
  | 'MissingCredentials'
  | 'TooManyRequests'
  | 'NetworkError'
  | 'ServerError'
  | 'UnknownError';

export class AuthError extends Error {
  type: AuthErrorType;
  status?: number;
  constructor(type: AuthErrorType, message: string, status?: number) {
    super(message);
    this.name = 'AuthError';
    this.type = type;
    this.status = status;
  }
}

export interface PasswordAuthTokens {
  accessToken: string;
  /** Some flows return an id_token alongside the access_token (Cognito IdP
   *  conventions). The Libertify auth proxy currently only ships
   *  access_token, but we keep this optional for forward-compat. */
  idToken?: string;
  /** The Libertify auth proxy does NOT return a refresh_token — when the
   *  access_token expires the user is asked to sign in again. */
  refreshToken?: string;
  expiresIn?: number;
  obtainedAt: number;
  tokenType?: string;
}

interface ApiTokenResponse {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  error?: string;
  message?: string;
  /** Libertify's API uses FastAPI which wraps errors in
   *  `{ detail: { code, message } }` (plain string for HTTPException, but
   *  the auth proxy returns the structured object). Both shapes handled. */
  detail?: string | { code?: string; message?: string };
}

function extractErrorMessage(parsed: ApiTokenResponse | null, status: number): string {
  if (!parsed) return `Sign-in failed (HTTP ${status})`;
  if (typeof parsed.detail === 'object' && parsed.detail) {
    return parsed.detail.message || parsed.detail.code || `Sign-in failed (HTTP ${status})`;
  }
  return (
    parsed.message ||
    (typeof parsed.detail === 'string' ? parsed.detail : undefined) ||
    parsed.error ||
    `Sign-in failed (HTTP ${status})`
  );
}

export async function signInWithPassword(
  env: Env,
  username: string,
  password: string
): Promise<PasswordAuthTokens> {
  const u = username.trim();
  if (!u || !password) {
    throw new AuthError('MissingCredentials', 'Email and password are required.');
  }

  const { apiBase } = getEndpoints(env);
  const body = new URLSearchParams({ username: u, password });

  let resp: Response;
  try {
    resp = await fetch(`${apiBase}/api/tokens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: body.toString(),
    });
  } catch (e) {
    throw new AuthError('NetworkError', e instanceof Error ? e.message : String(e));
  }

  let parsed: ApiTokenResponse | null = null;
  try {
    parsed = (await resp.json()) as ApiTokenResponse;
  } catch {
    /* leave null — fall through to status-based handling */
  }

  if (!resp.ok || !parsed?.access_token) {
    const message = extractErrorMessage(parsed, resp.status);
    if (resp.status === 401 || resp.status === 403) {
      throw new AuthError('InvalidCredentials', 'Incorrect email or password.', resp.status);
    }
    if (resp.status === 429) {
      throw new AuthError('TooManyRequests', 'Too many attempts — try again in a minute.', resp.status);
    }
    if (resp.status >= 500) {
      throw new AuthError('ServerError', message, resp.status);
    }
    throw new AuthError('UnknownError', message, resp.status);
  }

  return {
    accessToken: parsed.access_token,
    idToken: parsed.id_token,
    refreshToken: parsed.refresh_token,
    expiresIn: parsed.expires_in,
    obtainedAt: Date.now(),
    tokenType: parsed.token_type,
  };
}

// =============================================================================
// Token storage (sessionStorage — cleared when the tab closes)
// =============================================================================

const TOKENS_KEY = 'libertify_pg_tokens';

export function saveTokens(tokens: PasswordAuthTokens): void {
  try {
    sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
  } catch {
    /* sessionStorage may be disabled */
  }
}

export function loadTokens(): PasswordAuthTokens | null {
  try {
    const raw = sessionStorage.getItem(TOKENS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PasswordAuthTokens;
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  try {
    sessionStorage.removeItem(TOKENS_KEY);
  } catch {
    /* ignore */
  }
}

export function isExpired(tokens: PasswordAuthTokens): boolean {
  // Try the JWT exp claim first — it's exact.
  try {
    const [, payloadB64] = tokens.accessToken.split('.');
    if (payloadB64) {
      const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
      if (typeof payload.exp === 'number') {
        return Math.floor(Date.now() / 1000) >= payload.exp - 30;
      }
    }
  } catch {
    /* fall through */
  }
  if (!tokens.expiresIn) return false;
  return Date.now() >= tokens.obtainedAt + (tokens.expiresIn - 30) * 1000;
}

// =============================================================================
// Token claims helper
// =============================================================================

export interface TokenClaims {
  email?: string;
  name?: string;
  sub?: string;
  cognitoUsername?: string;
  exp?: number;
}

export function parseTokenClaims(jwt: string): TokenClaims {
  try {
    const [, payloadB64] = jwt.split('.');
    if (!payloadB64) return {};
    const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
    return {
      email: payload.email,
      name: payload.name || payload.given_name,
      sub: payload.sub,
      cognitoUsername: payload['cognito:username'],
      exp: payload.exp,
    };
  } catch {
    return {};
  }
}
